/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getApiPath } from "../utils";

export interface EulaLatestRes {
	body: string;
	title: string;
	key: string;
	version: number;
	lastModifiedTimestamp: string;
}

export interface EulaAcceptedRes {
	accepted?: boolean;
}

export interface EulaAcceptRes {
	accepted: boolean;
}

export interface EulaAcceptQueryProps {
	key: string;
	accountId: string;
	version: number;
	locale?: string;
}

export type EulaAcceptedQueryProps = Omit<EulaAcceptQueryProps, "version">;
export const createEulaKey = (request: EulaAcceptQueryProps | EulaAcceptedQueryProps): string => {
	return JSON.stringify(request);
};

export const getEulaData = async (key: string, serverUrl?: string): Promise<EulaLatestRes> => {
	if (!key) {
		throw new Error("getEulaData() requires key");
	}

	try {
		console.info("EulaFetch.getEulaData() fetching content", { key });

		const response = await fetch(
			`${getApiPath(serverUrl)}/eula?` +
				new URLSearchParams({
					eulaId: key,
					locale: "en",
				}).toString(),
			{
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				credentials: "include",
			},
		);
		const data: EulaLatestRes = await response.json();
		console.info("EulaFetch.getEulaData() success", { key: data.key });
		return data;
	} catch (err) {
		console.error("getEulaData() failed to load", { key });
		throw err;
	}
};

export const getEulaAccepted = async (eulaAcceptedRequestStr: string, serverUrl?: string): Promise<EulaAcceptedRes> => {
	let eulaAcceptedRequest: EulaAcceptedQueryProps;
	try {
		eulaAcceptedRequest = JSON.parse(eulaAcceptedRequestStr);
	} catch (e) {
		console.error("getEulaAccepted() invalid request");
		throw e;
	}

	const { key, accountId } = eulaAcceptedRequest;

	if (!key || !accountId) {
		throw new Error("getEulaAccepted() requires key, accountId");
	}

	try {
		const response = await fetch(
			`${getApiPath(serverUrl)}/eula/accept?` +
				new URLSearchParams({
					eulaId: key,
					locale: "en",
				}).toString(),
			{
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				credentials: "include",
			},
		);
		const data: EulaAcceptedRes = await response.json();
		return data;
	} catch (err) {
		console.error("getEulaAccepted() failed to load", { key });
		throw err;
	}
};

export const acceptEula = async (eulaAcceptRequestStr: string, serverUrl?: string): Promise<EulaAcceptRes> => {
	let eulaAcceptRequest: EulaAcceptQueryProps;
	try {
		eulaAcceptRequest = JSON.parse(eulaAcceptRequestStr);
	} catch (e) {
		console.info("acceptEula() invalid request");
		throw e;
	}

	const { key, accountId, version } = eulaAcceptRequest;

	if (!key || !accountId || !version) {
		throw new Error("acceptEula() requires key, acountId, and version");
	}

	try {
		const response = await fetch(
			`${getApiPath(serverUrl)}/eula/accept?` +
				new URLSearchParams({
					eulaId: key,
					locale: "en",
					version: version.toString(),
				}).toString(),
			{
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				credentials: "include",
			},
		);
		const data: EulaAcceptRes = await response.json();
		return data;
	} catch (err) {
		console.error("acceptEula() failed to load", { key });
		throw err;
	}
};
